import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { ArrowLeft, Sparkle } from "@phosphor-icons/react";

export default function TreatmentForm() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const editing = !!slug;
    const [name, setName] = useState("");
    const [category, setCategory] = useState("");
    const [description, setDescription] = useState("");
    const [aiPrompt, setAiPrompt] = useState("");
    const [aiLoading, setAiLoading] = useState(false);
    const [compounds, setCompounds] = useState([]);
    const [prep, setPrep] = useState([]);
    const [recup, setRecup] = useState([]);
    const [maint, setMaint] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/compounds").then((r) => setCompounds(r.data));
        if (editing) {
            api.get(`/treatments/${slug}`).then((r) => {
                const t = r.data;
                setName(t.name); setCategory(t.category); setDescription(t.description);
                setPrep(t.recommended_phases?.preparacion || []);
                setRecup(t.recommended_phases?.recuperacion || []);
                setMaint(t.recommended_phases?.mantenimiento || []);
            });
        }
    }, [slug, editing]);

    const toggle = (list, setList, v) => setList(list.includes(v) ? list.filter((x)=>x!==v) : [...list, v]);

    const aiFill = async () => {
        if (!name.trim()) { toast.error("Pon primero el nombre del tratamiento"); return; }
        setAiLoading(true);
        try {
            const { data } = await api.post("/treatments/ai-generate", { name, prompt: aiPrompt || null });
            if (data.category) setCategory(data.category);
            if (data.description) setDescription(data.description);
            if (data.recommended_phases) {
                setPrep(data.recommended_phases.preparacion || []);
                setRecup(data.recommended_phases.recuperacion || []);
                setMaint(data.recommended_phases.mantenimiento || []);
            }
            toast.success("Ficha rellenada con IA — revisa antes de guardar");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error generando ficha");
        } finally { setAiLoading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = {
            name, category, description,
            recommended_phases: { preparacion: prep, recuperacion: recup, mantenimiento: maint },
        };
        try {
            const r = editing
                ? await api.patch(`/treatments/${slug}`, payload)
                : await api.post("/treatments", payload);
            toast.success(editing ? "Tratamiento actualizado" : "Tratamiento creado");
            navigate(`/tratamientos/${r.data.slug}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error al guardar");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fade-up">
            <PageHeader
                eyebrow={editing ? "Editar tratamiento" : "Nuevo tratamiento"}
                title={editing ? name || "Editar tratamiento" : "Añadir tratamiento estético"}
                lead="Define un procedimiento y los principios activos sugeridos para preparación, recuperación inmediata y mantenimiento."
            >
                <div className="mt-4">
                    <Link to="/tratamientos" className="text-sm text-[var(--ink-2)] inline-flex items-center gap-1 hover:text-[var(--ink)]"><ArrowLeft size={14}/> Volver a tratamientos</Link>
                </div>
            </PageHeader>

            <form onSubmit={submit} className="px-8 md:px-12 py-8 max-w-5xl space-y-6">
                <div className="border hairline bg-white p-6 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="label-eyebrow">Datos básicos</div>
                        {!editing && (
                            <button type="button" onClick={aiFill} disabled={aiLoading || !name.trim()} className="px-3 py-1.5 text-xs border hairline bg-[var(--cream)] hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="treatment-aifill-button">
                                <Sparkle size={12} weight="bold"/> {aiLoading ? "Generando ficha…" : "Rellenar con IA"}
                            </button>
                        )}
                    </div>
                    <Field label="Nombre *"><input required className="i" value={name} onChange={(e)=>setName(e.target.value)} data-testid="treatment-name-input"/></Field>
                    {!editing && (
                        <Field label="Pista para la IA (opcional)">
                            <input className="i" value={aiPrompt} onChange={(e)=>setAiPrompt(e.target.value)} placeholder="Ej: enfoque post-procedimiento, paciente >50 años…" data-testid="treatment-aiprompt-input"/>
                        </Field>
                    )}
                    <Field label="Categoría *"><input required className="i" value={category} onChange={(e)=>setCategory(e.target.value)} placeholder="Inyectables / Energía / Peelings / Hilos / Bioestimulación" data-testid="treatment-category-input"/></Field>
                    <Field label="Descripción *"><textarea required rows={3} className="i resize-none" value={description} onChange={(e)=>setDescription(e.target.value)} data-testid="treatment-description-input"/></Field>
                </div>

                <PhaseSelector title="Preparación" compounds={compounds} selected={prep} onToggle={(v)=>toggle(prep,setPrep,v)} testid="prep"/>
                <PhaseSelector title="Recuperación inmediata" compounds={compounds} selected={recup} onToggle={(v)=>toggle(recup,setRecup,v)} testid="recup"/>
                <PhaseSelector title="Mantenimiento" compounds={compounds} selected={maint} onToggle={(v)=>toggle(maint,setMaint,v)} testid="maint"/>

                <div className="border-t hairline pt-6 flex justify-end">
                    <button type="submit" disabled={submitting} className="btn-primary" data-testid="treatment-submit-button">
                        {submitting ? "Guardando…" : editing ? "Guardar cambios" : "Crear tratamiento"}
                    </button>
                </div>
            </form>
            <style>{`.i{width:100%;padding:9px 12px;border:1px solid var(--line);background:#fff;font-size:.875rem;outline:none}.i:focus{border-color:var(--sage)}`}</style>
        </div>
    );
}

function Field({ label, children }) {
    return (<div><label className="text-xs text-[var(--ink-2)] block mb-1.5">{label}</label>{children}</div>);
}

function PhaseSelector({ title, compounds, selected, onToggle, testid }) {
    return (
        <div className="border hairline bg-white p-6">
            <div className="label-eyebrow mb-3">{title} · {selected.length} seleccionados</div>
            <div className="max-h-56 overflow-y-auto flex flex-wrap gap-2">
                {compounds.map((c) => {
                    const active = selected.includes(c.slug);
                    return (
                        <button
                            key={c.slug}
                            type="button"
                            onClick={() => onToggle(c.slug)}
                            className={`text-xs px-3 py-1.5 border hairline transition-colors ${active ? "bg-[var(--sage)] text-white border-[var(--sage)]" : "bg-white text-[var(--ink-2)] hover:bg-[var(--cream)]"}`}
                            data-testid={`treatment-${testid}-${c.slug}`}
                        >{c.name}</button>
                    );
                })}
            </div>
        </div>
    );
}
